import React, { useState, useMemo } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth, db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

function parseDisplayName(displayName) {
  if (!displayName) return { nombre: "", apellido: "" };

  const partes = displayName.trim().split(/\s+/);
  if (partes.length === 1) return { nombre: partes[0], apellido: "" };
  if (partes.length === 2) return { nombre: partes[0], apellido: partes[1] };

  return {
    nombre: partes.slice(0, 2).join(" "),
    apellido: partes.slice(2).join(" "),
  };
}

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showCreds, setShowCreds] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  const canSubmitCreds = useMemo(() => {
    return form.email.trim().length > 0 && form.password.trim().length > 0;
  }, [form.email, form.password]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await signInWithEmailAndPassword(auth, form.email, form.password);
      navigate("/");
    } catch (err) {
      console.error("❌ Error al iniciar sesión:", err.code, err.message);
      setError("Correo o contraseña incorrectos.");
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    setError("");

    try {
      const result = await signInWithPopup(auth, provider);
      const gUser = result.user;

      if (!gUser?.email) {
        setError("Tu cuenta de Google no entregó un correo. Prueba con otra cuenta.");
        return;
      }

      const emailKey = gUser.email.toLowerCase().trim();
      const ref = doc(db, "usuarios", emailKey);
      const snap = await getDoc(ref);

      // 🟦 EXISTE PACIENTE MANUAL → Solo actualizar UID
      if (snap.exists()) {
        await setDoc(
          ref,
          {
            uid: gUser.uid,
            origen: "manual+google",
            actualizadoEn: serverTimestamp(),
          },
          { merge: true }
        );

        navigate("/");
        return;
      }

      // 🟩 NO EXISTÍA → Crear perfil con nombre normalizado
      const { nombre, apellido } = parseDisplayName(gUser.displayName || "");

      await setDoc(
        ref,
        {
          uid: gUser.uid,
          correo: emailKey,

          nombres: nombre?.trim() || "",
          apellidos: apellido?.trim() || "",
          rut: "",
          celular: "",
          t_emergencia: "",

          primerasSesiones: {
            kinesiologia: false,
            entrenamiento: false,
            recovery: false,
            masajes: false,
          },
          origen: "google",
          creadoEn: serverTimestamp(),
        },
        { merge: true }
      );

      // 👉 Redirigir a completar datos SOLO primera vez
      navigate("/completar-perfil");
    } catch (err) {
      console.error("Error al iniciar con Google:", err);
      setError("No se pudo iniciar sesión con Google.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#E5E5E5]">
      <div className="w-full max-w-md rounded-3xl bg-white/60 backdrop-blur-xl shadow-[0_18px_50px_-10px_rgba(58,106,255,0.25)] ring-1 ring-white/30 p-8">
        <h1 className="text-3xl font-extrabold text-center text-[#0A1440] mb-6">
          Iniciar sesión
        </h1>

        {/* Error */}
        {error && (
          <p className="text-red-600 text-sm font-medium text-center mb-4">
            {error}
          </p>
        )}

        {/* ✅ GOOGLE PRIMERO */}
          <button
            onClick={handleGoogleLogin}
            className="
              w-full py-3 rounded-xl
              bg-white border border-[#dadce0]
              text-[#3c4043] font-semibold
              hover:bg-[#f8f9fa]
              transition
              flex items-center justify-center gap-3
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.651 32.657 29.233 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 13 24 13c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4c-7.682 0-14.347 4.337-17.694 10.691z"/>
              <path fill="#4CAF50" d="M24 44c5.176 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.18 35.091 26.715 36 24 36c-5.212 0-9.617-3.329-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.793 2.237-2.231 4.166-4.084 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>

            <span>Iniciar sesión con Google</span>
          </button>

        {/* Divider */}
        <div className="flex items-center my-6">
          <hr className="flex-grow border-gray-300" />
          <span className="mx-4 text-gray-600 text-sm">o</span>
          <hr className="flex-grow border-gray-300" />
        </div>

        {/* ✅ BOTÓN QUE DESPLIEGA CREDENCIALES */}
        <button
          type="button"
          onClick={() => setShowCreds((v) => !v)}
          className="
            w-full py-3 rounded-xl
            bg-[#06b6fa] text-white font-semibold
            hover:bg-blue-700 transition
          "
        >
          {showCreds ? "Ocultar credenciales" : "Inicia sesión con tus credenciales"}
        </button>

        {/* ✅ PANEL DESPLEGABLE */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${showCreds ? "max-h-[520px] opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"}
          `}
        >
          <form onSubmit={handleEmailLogin} className="space-y-4">
            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Correo electrónico"
                value={form.email}
                onChange={handleChange}
                required={showCreds}
                className="
                  w-full px-4 py-3 rounded-xl bg-white/90 border border-blue-100
                  focus:ring-2 focus:ring-[#3A6AFF] focus:outline-none
                "
              />
            </div>

            {/* Password */}
            <div>
              <input
                type="password"
                name="password"
                placeholder="Contraseña"
                value={form.password}
                onChange={handleChange}
                required={showCreds}
                className="
                  w-full px-4 py-3 rounded-xl bg-white/90 border border-blue-100
                  focus:ring-2 focus:ring-[#3A6AFF] focus:outline-none
                "
              />
            </div>

            {/* Recuperar contraseña */}
            <div className="flex justify-between text-sm text-[#0A1440]">
              <span>¿Olvidaste tu contraseña?</span>
              <button
                type="button"
                onClick={() => navigate("/recuperar")}
                className="text-[#06b6fa] hover:underline"
              >
                Recuperar
              </button>
            </div>

            {/* Botón login credenciales */}
            <button
              type="submit"
              disabled={!canSubmitCreds}
              className={`
                w-full py-3 rounded-xl font-semibold transition
                ${canSubmitCreds ? "bg-[#0b2052] text-white hover:opacity-90" : "bg-gray-300 text-gray-600 cursor-not-allowed"}
              `}
            >
              Iniciar sesión
            </button>
          </form>
        </div>

        {/* Registro (si lo quieres mantener, si no lo comentas) */}
        <p className="text-center text-sm mt-6 text-[#0A1440]">
          ¿No tienes una cuenta?{" "}
          <button
            onClick={() => navigate("/registro")}
            className="text-[#06b6fa] hover:underline"
          >
            Regístrate aquí
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
